/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import type { TableCredit } from "@lemmonhq/types";
import { formatPrice } from "@lemmonhq/orders";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "../../providers";
import type { TableCreditLedgerEntry } from "@app/api/trpc/routers/table-credit";

export type TableCreditSheetProps = {
  tableId: number;
  tableName?: string;
  credit: TableCredit;
  currency: string;
  onClose: () => void;
  onAddCredit: () => void;
};

const ENTRY_ICON: Record<string, string> = {
  load: "add_card",
  redemption: "restaurant",
  redemption_reversal: "undo",
  forfeit: "lock",
  move: "swap_horiz",
  void: "block",
};

const formatTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("fr-CH", {
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
};

/** A table's credit tonight: every top-up, what each order used, reversals. */
export const TableCreditSheet = defineComponent<TableCreditSheetProps>({
  name: "TableCreditSheet",
  props: ["tableId", "tableName", "credit", "currency", "onClose", "onAddCredit"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const entries = ref<TableCreditLedgerEntry[] | null>(null);
    const error = ref<string | null>(null);

    onMounted(async () => {
      try {
        entries.value = await trpc.tableCredit.ledger.query({
          tableId: props.tableId,
        });
      } catch (e: any) {
        error.value = e?.message || String(e);
      }
    });

    const money = (value: number | string) =>
      formatPrice(Number(value), true, 2, props.currency);

    const label = (entry: TableCreditLedgerEntry) => {
      if (entry.type === "redemption" && entry.order_ids.length > 0)
        return t("pos.table_credit.entry_order").replace(
          "{0}",
          entry.order_ids.map((id) => `#${id}`).join(", "),
        );
      if (entry.type === "move" && entry.note) return entry.note;
      return t(`pos.table_credit.entry_${entry.type}`);
    };

    const signed = (entry: TableCreditLedgerEntry) => {
      if (entry.type === "move") return "";
      const positive =
        entry.type === "load" || entry.type === "redemption_reversal";
      return `${positive ? "+" : "−"} ${money(entry.amount)}`;
    };

    return () => (
      <LemmonModal
        title={
          props.tableName
            ? `${t("pos.table_credit.title")} · ${props.tableName}`
            : t("pos.table_credit.title")
        }
        onClose={props.onClose}
      >
        <div class="flex min-h-0 flex-1 flex-col gap-3 p-4">
          <div class="grid grid-cols-2 gap-2">
            <div class="rounded-xl border bg-base-200 px-3 py-2">
              <div class="text-xl font-semibold text-accent">
                {money(props.credit.balance)}
              </div>
              <div class="text-xs text-base-content/55">
                {t("pos.table_credit.balance")}
              </div>
            </div>
            <div class="rounded-xl border bg-base-200 px-3 py-2">
              <div class="text-xl font-semibold">
                {money(props.credit.loaded)}
              </div>
              <div class="text-xs text-base-content/55">
                {t("pos.table_credit.loaded_tonight")}
              </div>
            </div>
          </div>

          <div class="min-h-0 flex-1 overflow-y-auto">
            {error.value ? (
              <div class="alert alert-error">{error.value}</div>
            ) : entries.value === null ? (
              <Loading />
            ) : (
              <ul class="flex flex-col divide-y divide-base-300">
                {entries.value.map((entry) => (
                  <li key={entry.id} class="flex items-center gap-3 py-2">
                    <span class="material-symbols-outlined text-base text-base-content/55">
                      {ENTRY_ICON[entry.type] || "receipt"}
                    </span>
                    <div class="flex flex-1 flex-col">
                      <span class="text-sm">{label(entry)}</span>
                      <span class="text-xs text-base-content/55">
                        {formatTime(entry.created_at)}
                      </span>
                    </div>
                    <span class="text-sm font-medium">{signed(entry)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            class="btn btn-primary"
            onClick={() => props.onAddCredit()}
          >
            <span class="material-symbols-outlined">add</span>
            {t("pos.table_credit.add_credit")}
          </button>
        </div>
      </LemmonModal>
    );
  },
});
